import React, { useState, useMemo } from "react";
import {
  Container,
  Table,
  Badge,
  Form,
  Row,
  Col,
  Button,
  Modal,
} from "react-bootstrap";
import {
  FaPhoneAlt,
  FaHistory,
  FaSearch,
  FaFilter,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaSpinner,
  FaTrash,
  FaDownload,
  FaEye,
} from "react-icons/fa";
import { callsAPI } from "../services/api";
import { toast } from "react-toastify";
import "../styles/CallHistory.css";
import LoadingSpinner from "../components/LoadingSpinner";
import { useInfiniteQuery } from "@tanstack/react-query";

export const CALL_HISTORY_QUERY_KEY = ["callHistoryData"];

export const fetchCallHistory = async ({ pageParam = null }) => {
  try {
    const params = { page_size: 100 };
    if (pageParam) params.cursor = pageParam;

    const response = await callsAPI.getConversations(params);
    const conversations = response.data.conversations || [];

    return {
      calls: conversations.map((conv) => ({
        id: conv.conversation_id,
        lead_name: conv.lead_name || "Unknown",
        lead_phone: conv.lead_phone || "N/A",
        user_name: conv.agent_name || "AI Agent",
        status:
          conv.status === "done"
            ? "success"
            : conv.status === "failed"
            ? "missed"
            : "unknown",
        duration: conv.call_duration_secs || 0,
        started_at: new Date(conv.start_time_unix_secs * 1000).toISOString(),
        summary:
          conv.transcript_summary || conv.summary || "No summary available",
        proposal_status: conv.proposal_status || "unknown",
        transcript: null,
        conversation_id: conv.conversation_id,
        direction: conv.direction || "outbound",
      })),
      nextCursor: response.data.next_cursor,
      hasMore: response.data.has_more,
    };
  } catch (error) {
    console.error("Error fetching call history:", error);
    toast.error("Failed to load latest data");
    return { calls: [], nextCursor: null, hasMore: false };
  }
};

const formatDuration = (duration) => {
  if (!duration) return "0:00";
  const mins = Math.floor(duration / 60);
  const secs = duration % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

const formatDateTime = (timestamp) => {
  if (!timestamp) return { date: "-", time: "-" };
  const d = new Date(timestamp);
  return {
    date: d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    time: d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    }),
  };
};

const getProposalStyle = (status) => {
  const base = {
    padding: "6px 14px",
    borderRadius: "999px",
    fontSize: "0.8rem",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.2)",
    textTransform: "capitalize",
    color: "#fff",
    whiteSpace: "nowrap",
  };

  switch ((status || "").toLowerCase()) {
    case "start":
    case "ready_for_proposal":
    case "ready for proposal":
      return { ...base, background: "linear-gradient(45deg,#10b981,#059669)" };
    case "delay":
    case "call_later":
    case "call later":
      return { ...base, background: "linear-gradient(45deg,#f59e0b,#d97706)" };
    case "decline":
    case "cancelled":
      return { ...base, background: "linear-gradient(45deg,#ef4444,#dc2626)" };
    default:
      return { ...base, background: "linear-gradient(45deg,#94a3b8,#64748b)" };
  }
};

const getStatusStyle = (status) => {
  const base = {
    padding: "6px 14px",
    borderRadius: "999px",
    fontSize: "0.8rem",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.2)",
    textTransform: "capitalize",
    color: "#fff",
    whiteSpace: "nowrap",
  };

  switch ((status || "").toLowerCase()) {
    case "success":
      return { ...base, background: "linear-gradient(45deg,#10b981,#059669)" };
    case "missed":
      return { ...base, background: "linear-gradient(45deg,#ef4444,#dc2626)" };
    default:
      return { ...base, background: "linear-gradient(45deg,#94a3b8,#64748b)" };
  }
};

const getStatusIcon = (status) => {
  switch ((status || "").toLowerCase()) {
    case "success":
      return <FaCheckCircle />;
    case "missed":
      return <FaTimesCircle />;
    default:
      return <FaClock />;
  }
};

const CallRow = ({
  call,
  expanded,
  onToggleExpand,
  onDownloadAudio,
  onDeleteClick,
  transcripts,
  loadingTranscript,
  onEnsureTranscript,
}) => {
  const { date, time } = formatDateTime(call.started_at);

  const handleToggle = async () => {
    const willExpand = !expanded;
    await onToggleExpand(call.id);
    if (willExpand) onEnsureTranscript(call);
  };

  return (
    <>
      <tr
        className={`call-row ${expanded ? "table-active" : ""}`}
        style={{ cursor: "pointer" }}
        onClick={handleToggle}
      >
        <td className="text-center">
          <div className="fw-semibold">{call.lead_name}</div>
        </td>
        <td className="text-center">
          <span className="mono">{call.lead_phone}</span>
        </td>
        <td className="text-center">
          <span className="small text-muted">{call.user_name}</span>
        </td>
        <td className="text-center">
          <span style={getProposalStyle(call.proposal_status)}>
            {call.proposal_status}
          </span>
        </td>
        <td className="text-center">
          <span style={getStatusStyle(call.status)}>
            {getStatusIcon(call.status)}
            {call.status}
          </span>
        </td>
        <td className="text-center">{formatDuration(call.duration)}</td>
        <td className="text-center text-nowrap">
          {date} <span className="text-muted small">{time}</span>
        </td>
        <td className="text-center" onClick={(e) => e.stopPropagation()}>
          <div className="d-flex flex-wrap gap-2 justify-content-center">
            <Button
              size="sm"
              variant={expanded ? "secondary" : "primary"}
              className="d-flex align-items-center"
              onClick={handleToggle}
            >
              <FaEye className="me-1" />
              {expanded ? "Hide" : "View"}
            </Button>

            <Button
              size="sm"
              variant="success"
              className="d-flex align-items-center"
              onClick={() => onDownloadAudio(call)}
            >
              <FaDownload />
            </Button>

            <Button
              size="sm"
              variant="danger"
              className="d-flex align-items-center"
              onClick={() => onDeleteClick(call)}
            >
              <FaTrash />
            </Button>
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="expanded-row">
          <td colSpan={8} className="p-0">
            <div className="expanded-content p-3 p-md-4 bg-light">
              {call.summary && (
                <div className="summary-block bg-white rounded p-3 mb-3 mb-md-4">
                  <h6 className="text-primary mb-3 d-flex align-items-center">
                    <FaCheckCircle className="me-2 text-success" />
                    Call Summary
                  </h6>
                  <p className="mb-0">{call.summary}</p>
                </div>
              )}

              <div className="transcript-container bg-white rounded p-3">
                <h6 className="text-primary mb-3 d-flex align-items-center">
                  <FaHistory className="me-2" />
                  Call Transcript
                </h6>

                {loadingTranscript && !transcripts[call.id] ? (
                  <div className="text-center py-4">
                    <FaSpinner className="spinner text-primary me-2" />
                    Loading transcript...
                  </div>
                ) : (
                  <pre className="transcript-text mb-0">
                    {transcripts[call.id] || "No transcript available."}
                  </pre>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const CallCardMobile = ({
  call,
  expanded,
  onToggle,
  onEnsureTranscript,
  transcript,
  loadingTranscript,
  onDownloadAudio,
  onDeleteClick,
}) => {
  const { date, time } = formatDateTime(call.started_at);

  const handleExpand = async () => {
    onToggle(call.id);
    if (!expanded) onEnsureTranscript(call);
  };

  return (
    <div className="call-card-mobile" onClick={handleExpand}>
      <div className="call-card-header">
        <div>
          <h5 className="mb-1">{call.lead_name}</h5>
          <small className="text-muted">{call.lead_phone}</small>
        </div>

        <span style={getStatusStyle(call.status)}>
          {getStatusIcon(call.status)}
          {call.status}
        </span>
      </div>

      <div className="call-card-meta">
        <span>{date}</span>
        <span>{time}</span>
        <span>{formatDuration(call.duration)}</span>
      </div>

      {expanded && (
        <div className="call-card-details">
          <p>
            <strong>Agent:</strong> {call.user_name}
          </p>

          <p>
            <strong>Proposal:</strong>{" "}
            <span style={getProposalStyle(call.proposal_status)}>
              {call.proposal_status}
            </span>
          </p>

          {call.summary && (
            <div className="call-summary">
              <strong>Summary</strong>
              <p>{call.summary}</p>
            </div>
          )}

          <div className="call-transcript">
            <strong>Transcript</strong>
            {loadingTranscript && !transcript ? (
              <p>Loading transcript…</p>
            ) : (
              <pre>{transcript || "No transcript available"}</pre>
            )}
          </div>

          <div className="call-card-actions">
            <Button
              size="sm"
              variant="success"
              onClick={(e) => {
                e.stopPropagation();
                onDownloadAudio(call);
              }}
            >
              <FaDownload /> Audio
            </Button>

            <Button
              size="sm"
              variant="danger"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(call);
              }}
            >
              <FaTrash /> Delete
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const LoadMoreButton = ({ hasNextPage, fetchNextPage, isFetchingNextPage }) => {
  if (!hasNextPage) return null;

  return (
    <div className="d-flex justify-content-center mt-4">
      <Button
        variant="primary"
        onClick={() => fetchNextPage()}
        disabled={isFetchingNextPage}
      >
        {isFetchingNextPage ? (
          <>
            <FaSpinner className="spinner me-2" />
            Loading...
          </>
        ) : (
          "Next"
        )}
      </Button>
    </div>
  );
};

const CallHistory = () => {
  const [expandedCallId, setExpandedCallId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ status: "" });
  const [loadingTranscript, setLoadingTranscript] = useState(false);
  const [transcripts, setTranscripts] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [callToDelete, setCallToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const {
    data,
    isLoading,
    isError,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: CALL_HISTORY_QUERY_KEY,
    queryFn: fetchCallHistory,
    getNextPageParam: (lastPage) =>
      lastPage?.hasMore ? lastPage.nextCursor : undefined,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
  });

  const calls = useMemo(() => {
    return data?.pages?.flatMap((page) => page.calls) || [];
  }, [data]);

  const filteredCalls = useMemo(() => {
    return calls.filter((call) => {
      if (!call) return false;

      const normalizedSearch = searchTerm.toLowerCase().trim();

      const matchesSearch =
        !normalizedSearch ||
        call.lead_name?.toLowerCase().includes(normalizedSearch) ||
        call.lead_phone?.includes(searchTerm.trim());

      const matchesStatus =
        !filters.status ||
        call.status?.toLowerCase() === filters.status.toLowerCase();

      return matchesSearch && matchesStatus;
    });
  }, [calls, searchTerm, filters]);

  const resetFilters = () => {
    setSearchTerm("");
    setFilters({ status: "" });
  };

  const hasActiveFilters = !!(searchTerm || filters.status);

  const handleToggleExpand = async (callId) => {
    setExpandedCallId((prev) => (prev === callId ? null : callId));
  };

  const ensureTranscriptLoaded = async (call) => {
    if (!call.conversation_id || transcripts[call.id]) return;

    setLoadingTranscript(true);

    try {
      const response = await callsAPI.getConversationDetails(
        call.conversation_id
      );
      const details = response.data;

      let formatted = "No transcript available";

      if (Array.isArray(details.transcript) && details.transcript.length > 0) {
        formatted = details.transcript
          .map((item) => {
            const role = item.role === "agent" ? "AI Agent" : "Customer";
            const message = item.message || "";
            const t = item.time_in_call_secs || 0;
            const mins = Math.floor(t / 60);
            const secs = t % 60;

            return `[${mins}:${secs
              .toString()
              .padStart(2, "0")}] ${role}: ${message}`;
          })
          .join("\n");
      }

      setTranscripts((prev) => ({ ...prev, [call.id]: formatted }));
    } catch (err) {
      console.error("Error fetching transcript:", err);
      toast.error("Failed to load transcript");
      setTranscripts((prev) => ({
        ...prev,
        [call.id]: "Failed to load transcript",
      }));
    } finally {
      setLoadingTranscript(false);
    }
  };

  const handleDeleteClick = (call) => {
    setCallToDelete(call);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!callToDelete) return;

    setDeleting(true);

    try {
      await callsAPI.deleteConversation(callToDelete.conversation_id);
      toast.success("Call deleted successfully");
      setShowDeleteModal(false);
      setCallToDelete(null);
      refetch();
    } catch (err) {
      console.error("Error deleting call:", err);
      toast.error("Failed to delete call");
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadAudio = async (call) => {
    try {
      toast.info("Downloading audio...");

      const response = await callsAPI.getConversationAudio(
        call.conversation_id
      );

      const blob = new Blob([response.data], { type: "audio/mpeg" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");

      a.href = url;
      a.download = `call_${call.lead_name}_${new Date(call.started_at)
        .toISOString()
        .slice(0, 10)}.mp3`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Audio downloaded successfully");
    } catch (err) {
      console.error("Error downloading audio:", err);

      if (err.response?.status === 404) {
        toast.error("Audio recording not available for this call");
      } else {
        toast.error("Failed to download audio");
      }
    }
  };

  const handleSyncNow = async () => {
    try {
      toast.info("Syncing latest calls...");
      await callsAPI.syncCalls(200);
      await refetch();
      toast.success("Sync complete");
    } catch (err) {
      console.error("Error syncing calls:", err);
      toast.error("Failed to sync calls");
    }
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading call history..." />;
  }

  if (isError) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center"
        style={{ minHeight: "60vh" }}
      >
        <p className="text-danger fw-bold mb-3">
          Failed to load call history.
        </p>
        <Button onClick={refetch}>Retry</Button>
      </div>
    );
  }

  return (
    <Container fluid className="dashboard-container p-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
        <div>
          <h1 className="h2 fw-bold mb-1" style={{ color: "#1e293b" }}>
            <FaPhoneAlt className="me-3" style={{ color: "#3b82f6" }} />
            Call History
          </h1>
          <p className="text-muted mb-0">
            View the history of all proposal-related calls and conversations.
          </p>
        </div>

        <Badge
          bg="primary"
          className="fs-6 px-4 py-2 rounded-pill"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
          }}
        >
          <FaHistory className="me-2" />
          {filteredCalls.length} Calls
        </Badge>
      </div>

      <div className="search-filter-container p-3 p-md-4 mb-4">
        <Row className="g-3 align-items-end">
          <Col md={4}>
            <Form.Group>
              <Form.Label className="fw-bold">
                <FaSearch className="me-2" />
                Search Lead
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Search by name or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label className="fw-bold">
                <FaFilter className="me-2" />
                Filter by Status
              </Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <option value="">All Statuses</option>
                <option value="success">Success</option>
                <option value="missed">Missed</option>
                <option value="unknown">Unknown</option>
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={2}>
            <Form.Group>
              <Form.Label className="invisible">Reset</Form.Label>
              <Button
                variant="outline-danger"
                className="w-100"
                disabled={!hasActiveFilters}
                onClick={resetFilters}
              >
                Reset Filters
              </Button>
            </Form.Group>
          </Col>
        </Row>
      </div>

      <div className="d-flex justify-content-end mb-3">
        <Button variant="primary" onClick={handleSyncNow}>
          <FaHistory className="me-2" />
          Sync Now
        </Button>
      </div>

      <div className="d-none d-md-block table-container">
        <div className="table-responsive">
          <Table hover className="mb-0 align-middle">
            <thead className="table-header">
              <tr>
                <th className="text-center">Lead Name</th>
                <th className="text-center">Phone</th>
                <th className="text-center">Agent</th>
                <th className="text-center">Proposal Status</th>
                <th className="text-center">Status</th>
                <th className="text-center">Duration</th>
                <th className="text-center">Started At</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredCalls.map((call) => (
                <CallRow
                  key={call.id}
                  call={call}
                  expanded={expandedCallId === call.id}
                  onToggleExpand={handleToggleExpand}
                  onDownloadAudio={handleDownloadAudio}
                  onDeleteClick={handleDeleteClick}
                  transcripts={transcripts}
                  loadingTranscript={loadingTranscript}
                  onEnsureTranscript={ensureTranscriptLoaded}
                />
              ))}
            </tbody>
          </Table>
        </div>

        <LoadMoreButton
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      <div className="d-md-none">
        {filteredCalls.map((call) => (
          <CallCardMobile
            key={call.id}
            call={call}
            expanded={expandedCallId === call.id}
            onToggle={handleToggleExpand}
            onEnsureTranscript={ensureTranscriptLoaded}
            transcript={transcripts[call.id]}
            loadingTranscript={loadingTranscript}
            onDownloadAudio={handleDownloadAudio}
            onDeleteClick={handleDeleteClick}
          />
        ))}

        <LoadMoreButton
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
      >
        <Modal.Header
          closeButton
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            border: "none",
          }}
        >
          <Modal.Title className="d-flex align-items-center">
            <FaTrash className="me-2" />
            Confirm Delete
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="text-center mb-3">
            <p>
              Are you sure you want to delete the call with{" "}
              <strong>{callToDelete?.lead_name}</strong>?
            </p>
            <p className="text-muted small mb-0">
              This action cannot be undone.
            </p>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            Cancel
          </Button>

          <Button
            variant="danger"
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <FaSpinner className="spinner me-2" />
                Deleting...
              </>
            ) : (
              <>
                <FaTrash className="me-2" />
                Delete
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <style>{`
        .expanded-content {
          border-top: 1px solid #e2e8f0;
        }

        .transcript-text {
          white-space: pre-wrap;
          font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
          font-size: 0.85rem;
          color: #1e293b;
        }

        .summary-block,
        .transcript-container {
          box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
        }

        .table-header th {
          background: #f8fafc;
          font-weight: 600;
          color: #1e293b;
          border-bottom: 2px solid #e2e8f0;
          white-space: nowrap;
        }

        .spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @media (max-width: 768px) {
          .table-responsive {
            border-radius: 0.5rem;
          }

          .table-header th {
            font-size: 0.75rem;
          }

          td, th {
            font-size: 0.8rem;
          }

          .expanded-content {
            padding: 1rem !important;
          }
        }
      `}</style>
    </Container>
  );
};

export default CallHistory;